import React, { useState, useEffect } from 'react';
import { Card, Avatar, Tag, Tabs, Form, Input, Button, Row, Col, Divider, message, Spin } from 'antd';
import { UserOutlined, MailOutlined, PhoneOutlined, BankOutlined, CalendarOutlined, ClockCircleOutlined, EditOutlined, LockOutlined, SafetyOutlined, DatabaseOutlined } from '@ant-design/icons';
import { useAuth } from '@/contexts/AuthContext';
import { usersAPI } from '@/services/api/users';
import { UserResponse, UpdateUserRequest, ChangePasswordRequest, RoleInfo } from '@/types/api';
import { Modal } from 'antd';
import './AccountManagement.css';


const AccountManagement: React.FC = () => {
  const [form] = Form.useForm();
  const [passwordForm] = Form.useForm();
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [rolePermissions, setRolePermissions] = useState<{ [key: string]: RoleInfo }>({});
  const [permissionLoading, setPermissionLoading] = useState(false);
  
  const { userInfo, logout, refreshUserInfo } = useAuth();
  const [userProfile, setUserProfile] = useState<UserResponse | null>(null);

  // 初始化用户信息（直接使用 AuthContext 中的 userInfo）
  const initUserProfile = () => {
    if (!userInfo) return;

    setLoading(true);
    // 将 userInfo 转换为 UserResponse 格式
    const userData: UserResponse = {
      id: userInfo.id,
      username: userInfo.username,
      name: userInfo.name,
      email: userInfo.email,
      phone: userInfo.phone,
      avatar: userInfo.avatar,
      bio: userInfo.bio,
      department: userInfo.department,
      role: userInfo.role,
      role_name: userInfo.role_name,
      status: userInfo.status,
      status_name: userInfo.status_name,
      created_at: userInfo.created_at,
      updated_at: userInfo.updated_at,
      last_login_at: userInfo.last_login_at,
    };
    setUserProfile(userData);
    form.setFieldsValue({
      name: userData.name,
      username: userData.username,
      email: userData.email,
      phone: userData.phone,
      department: userData.department,
    });
    setLoading(false);
  };

  // 获取角色权限信息
  const fetchRolePermissions = async () => {
    try {
      setPermissionLoading(true);
      const permissions = await usersAPI.getRolePermissions();
      setRolePermissions(permissions);
    } catch (error) {
      console.error('获取角色权限失败:', error);
      message.error('获取角色权限失败');
    } finally {
      setPermissionLoading(false);
    }
  };

  // 获取当前用户角色的权限
  const getCurrentUserRolePermissions = () => {
    if (!userInfo?.role || !Object.keys(rolePermissions).length) return null;
    return rolePermissions[userInfo.role];
  };

  // 初始化数据
  useEffect(() => {
    initUserProfile();
    fetchRolePermissions();
  }, [userInfo]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleSave = async (values: any) => {
    if (!userInfo?.id) return;

    try {
      setSaving(true);
      const updateData: UpdateUserRequest = {
        name: values.name,
        email: values.email,
        phone: values.phone,
        department: values.department,
      };

      const updatedUser = await usersAPI.updateCurrentUser(updateData);
      setUserProfile(updatedUser);
      setEditing(false);
      message.success('用户信息更新成功');
      // 刷新 AuthContext 中的用户信息
      await refreshUserInfo();
    } catch (error: any) {
      console.error('保存失败:', error);
      message.error(error.message || '保存失败，请重试');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setEditing(false);
    // 重置表单数据
    if (userProfile) {
      form.setFieldsValue({
        name: userProfile.name,
        username: userProfile.username,
        email: userProfile.email,
        phone: userProfile.phone,
        department: userProfile.department,
      });
    }
  };

  // 密码修改相关
  const handlePasswordSubmit = async (values: any) => {
    try {
      setChangingPassword(true);
      const passwordData: ChangePasswordRequest = {
        current_password: values.current_password,
        new_password: values.new_password,
      };
      
      await usersAPI.changePassword(passwordData);
      
      // 显示确认对话框
      Modal.confirm({
        title: '密码修改成功',
        content: '为了安全考虑，系统将要求您重新登录。是否现在跳转到登录页面？',
        okText: '立即登录',
        cancelText: '稍后登录',
        onOk: () => {
          // 清除本地存储的认证信息
          logout();
          message.success('请使用新密码重新登录');
        },
        onCancel: () => {
          message.info('请记住使用新密码重新登录');
        }
      });
      
      passwordForm.resetFields();
    } catch (error: any) {
      console.error('密码修改失败:', error);
      message.error(error.message || '密码修改失败，请重试');
    } finally {
      setChangingPassword(false);
    }
  };

  // 权限相关辅助函数
  const getPermissionIcon = (permission: string) => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'version:create': <DatabaseOutlined />,
      'version:manage': <DatabaseOutlined />,
      'version:view': <DatabaseOutlined />,
      'strategy:config': <SafetyOutlined />,
      'strategy:publish': <SafetyOutlined />,
      'user:manage': <UserOutlined />,
      'system:config': <SafetyOutlined />,
      'audit:view': <SafetyOutlined />,
      'progress:monitor': <SafetyOutlined />,
      'device:view': <DatabaseOutlined />,
      'package:verify': <SafetyOutlined />,
      'dashboard:view': <SafetyOutlined />,
      'api:view': <SafetyOutlined />,
    };
    return iconMap[permission] || <SafetyOutlined />;
  };

  const getPermissionName = (permission: string) => {
    const nameMap: { [key: string]: string } = {
      'version:create': '创建版本',
      'version:manage': '管理版本',
      'version:view': '查看版本',
      'strategy:config': '配置策略',
      'strategy:publish': '发布策略',
      'user:manage': '用户管理',
      'system:config': '系统配置',
      'audit:view': '审计日志',
      'progress:monitor': '进度监控',
      'device:view': '设备查看',
      'package:verify': '包验证',
      'dashboard:view': '仪表板',
      'api:view': 'API访问',
    };
    return nameMap[permission] || permission;
  };

  const getPermissionDescription = (permission: string) => {
    const descMap: { [key: string]: string } = {
      'version:create': '可以创建新的版本',
      'version:manage': '可以管理所有版本',
      'version:view': '可以查看版本信息',
      'strategy:config': '可以配置升级策略',
      'strategy:publish': '可以发布升级策略',
      'user:manage': '可以管理用户账号',
      'system:config': '可以配置系统参数',
      'audit:view': '可以查看审计日志',
      'progress:monitor': '可以监控升级进度',
      'device:view': '可以查看设备信息',
      'package:verify': '可以验证升级包',
      'dashboard:view': '可以访问仪表板',
      'api:view': '可以访问API接口',
    };
    return descMap[permission] || '系统权限';
  };

  if (loading) {
    return (
      <div className="account-management">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <p style={{ marginTop: '16px', color: 'var(--text-primary)' }}>加载用户信息中...</p>
        </div>
      </div>
    );
  }

  if (!userProfile) {
    return (
      <div className="account-management">
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <p style={{ color: 'var(--text-primary)' }}>无法加载用户信息</p>
        </div>
      </div>
    );
  }

  return (
    <div className="account-management">
      <div className="account-header">
        <h1 className="account-title">账号管理</h1>
        <p className="account-subtitle">管理您的个人资料和账号设置</p>
      </div>

      <Row gutter={24}>
        {/* 左侧用户信息 */}
        <Col xs={24} lg={8}>
          <Card className="user-info-card">
            <div className="user-info-header">
              <h3>用户信息</h3>
            </div>
            <div className="user-profile">
              <Avatar 
                size={80} 
                src={userProfile.avatar} 
                icon={<UserOutlined />} 
                className="user-avatar" 
              />
              <div className="user-details">
                <h4 className="user-name">{userProfile.name}</h4>
                <p className="user-username">{userProfile.username}</p>
                <Tag color="red" className="role-tag">
                  <SafetyOutlined /> {userProfile.role_name}
                </Tag>
              </div>
            </div>
            <Divider />
            <div className="user-contact-info">
              <div className="info-item">
                <MailOutlined className="info-icon" />
                <span className="info-label">邮箱</span>
                <span className="info-value">{userProfile.email}</span>
              </div>
              <div className="info-item">
                <PhoneOutlined className="info-icon" />
                <span className="info-label">手机号</span>
                <span className="info-value">{userProfile.phone || '未设置'}</span>
              </div>
              <div className="info-item">
                <BankOutlined className="info-icon" />
                <span className="info-label">部门</span>
                <span className="info-value">{userProfile.department || '未设置'}</span>
              </div>
              <div className="info-item">
                <CalendarOutlined className="info-icon" />
                <span className="info-label">注册时间</span>
                <span className="info-value">{new Date(userProfile.created_at).toLocaleString()}</span>
              </div>
              <div className="info-item">
                <ClockCircleOutlined className="info-icon" />
                <span className="info-label">最后登录</span>
                <span className="info-value">
                  {userProfile.last_login_at 
                    ? new Date(userProfile.last_login_at).toLocaleString() 
                    : '从未登录'
                  }
                </span>
              </div>
            </div>
          </Card>
        </Col>

        {/* 右侧内容区域 */}
        <Col xs={24} lg={16}>
          <Card className="account-content-card">
            <Tabs 
              defaultActiveKey="profile" 
              className="account-tabs"
              items={[
                {
                  key: 'profile',
                  label: (
                    <span>
                      <UserOutlined />
                      个人资料
                    </span>
                  ),
                  children: (
                <div className="profile-section">
                  <div className="section-header">
                    <h3>编辑个人资料</h3>
                    <p>更新您的个人信息，这些信息将显示在您的账号中</p>
                  </div>
                  
                  <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSave}
                    className="profile-form"
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="姓名"
                          name="name"
                          rules={[{ required: true, message: '请输入姓名' }]}
                        >
                          <Input 
                            disabled={!editing} 
                            className={!editing ? 'readonly-input' : ''}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="用户名"
                          name="username"
                          rules={[{ required: true, message: '请输入用户名' }]}
                        >
                          <Input 
                            disabled={!editing} 
                            className={!editing ? 'readonly-input' : ''}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="邮箱地址"
                          name="email"
                          rules={[
                            { required: true, message: '请输入邮箱地址' },
                            { type: 'email', message: '请输入有效的邮箱地址' }
                          ]}
                        >
                          <Input 
                            disabled={!editing} 
                            className={!editing ? 'readonly-input' : ''}
                          />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="手机号"
                          name="phone"
                          rules={[{ required: true, message: '请输入手机号' }]}
                        >
                          <Input 
                            disabled={!editing} 
                            className={!editing ? 'readonly-input' : ''}
                          />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Form.Item
                      label="部门"
                      name="department"
                      rules={[{ required: true, message: '请输入部门' }]}
                    >
                      <Input 
                        disabled={!editing} 
                        className={!editing ? 'readonly-input' : ''}
                      />
                    </Form.Item>

                    {editing && (
                      <div className="form-actions">
                        <Button onClick={handleCancel} className="cancel-btn" disabled={saving}>
                          取消
                        </Button>
                        <Button 
                          type="primary" 
                          htmlType="submit" 
                          className="save-btn"
                          loading={saving}
                        >
                          {saving ? '保存中...' : '保存更改'}
                        </Button>
                      </div>
                    )}
                  </Form>

                  {!editing && (
                    <div className="edit-actions">
                      <Button 
                        type="primary" 
                        icon={<EditOutlined />} 
                        onClick={handleEdit}
                        className="edit-btn"
                      >
                        编辑资料
                      </Button>
                    </div>
                  )}
                </div>
                  )
                },
                {
                  key: 'security',
                  label: (
                    <span>
                      <LockOutlined />
                      安全设置
                    </span>
                  ),
                  children: (
                <div className="security-section">
                  <div className="section-header">
                    <h3>修改密码</h3>
                    <p>定期更新您的密码以确保账号安全</p>
                  </div>
                  
                  <Form
                    form={passwordForm}
                    layout="vertical"
                    onFinish={handlePasswordSubmit}
                    className="password-form"
                  >
                    <Row gutter={16}>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="当前密码"
                          name="current_password"
                          rules={[{ required: true, message: '请输入当前密码' }]}
                        >
                          <Input.Password placeholder="请输入当前密码" />
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12}>
                        <Form.Item
                          label="新密码"
                          name="new_password"
                          rules={[
                            { required: true, message: '请输入新密码' },
                            { min: 6, message: '密码长度至少6位' }
                          ]}
                        >
                          <Input.Password placeholder="请输入新密码" />
                        </Form.Item>
                      </Col>
                    </Row>
                    
                    <Form.Item
                      label="确认新密码"
                      name="confirm_password"
                      dependencies={['new_password']}
                      rules={[
                        { required: true, message: '请确认新密码' },
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            if (!value || getFieldValue('new_password') === value) {
                              return Promise.resolve();
                            }
                            return Promise.reject(new Error('两次输入的密码不一致'));
                          },
                        }),
                      ]}
                    >
                      <Input.Password placeholder="请再次输入新密码" />
                    </Form.Item>
                    
                    <div className="form-actions">
                      <Button 
                        type="primary" 
                        htmlType="submit" 
                        loading={changingPassword}
                        className="save-btn"
                      >
                        {changingPassword ? '修改中...' : '确认修改密码'}
                      </Button>
                    </div>
                  </Form>
                </div>
                  )
                },
                {
                  key: 'permissions',
                  label: (
                    <span>
                      <SafetyOutlined />
                      权限说明
                    </span>
                  ),
                  children: (
                <div className="permissions-section">
                  <div className="section-header">
                    <h3>权限说明</h3>
                    <p>了解您当前账号的权限范围</p>
                  </div>
                  
                  {permissionLoading ? (
                    <div style={{ textAlign: 'center', padding: '50px' }}>
                      <Spin size="large" />
                      <p style={{ marginTop: '16px', color: 'var(--text-primary)' }}>加载权限信息中...</p>
                    </div>
                  ) : (
                    <div className="permissions-content">
                      {(() => {
                        const currentRolePermissions = getCurrentUserRolePermissions();
                        if (!currentRolePermissions) {
                          return (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                              <p style={{ color: 'var(--text-primary)' }}>无法获取权限信息</p>
                              <p style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>
                                当前用户角色: {userInfo?.role || '未知'}
                              </p>
                            </div>
                          );
                        }

                        return (
                          <>
                            {/* 角色概览 */}
                            <Card className="role-overview-card" style={{ marginBottom: '24px' }}>
                              <div className="role-header">
                                <SafetyOutlined className="role-icon" />
                                <div className="role-info">
                                  <h4>{currentRolePermissions.name}</h4>
                                  <p>{currentRolePermissions.description}</p>
                                  <Tag color="blue" className="permission-count">
                                    拥有 {currentRolePermissions.permissions.length} 项权限
                                  </Tag>
                                </div>
                              </div>
                            </Card>

                            {/* 权限列表 */}
                            <div className="permissions-grid">
                              {currentRolePermissions.permissions.map((permission) => (
                                <Card key={permission} className="permission-item" size="small">
                                  <div className="permission-content">
                                    <div className="permission-icon">
                                      {getPermissionIcon(permission)}
                                    </div>
                                    <div className="permission-info">
                                      <h5>{getPermissionName(permission)}</h5>
                                      <p>{getPermissionDescription(permission)}</p>
                                    </div>
                                  </div>
                                </Card>
                              ))}
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  )}
                </div>
                  )
                }
              ]}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default AccountManagement;
